<template>
    <div>
        <div v-if="booking">
            <v-toolbar flat color="green" dark extended>
                <v-toolbar-title>
                    {{booking.courseTitle}} (BK-{{booking.id}}) - {{booking.contactCompany}}

                </v-toolbar-title>
                <UpdateStatusDialog v-if="booking.status" v-model="booking.status" :booking-id="booking.id" @updated="refreshBooking"/>


                <v-spacer></v-spacer>
                <v-toolbar-title>{{booking.totalPrice | toCurrency}}</v-toolbar-title>

                <v-img v-if="booking.iconUrl" :src="booking.iconUrl" max-height="50" max-width="50"/>
                <v-progress-linear
                        :active="updating || $apollo.loading"
                        :indeterminate="updating || $apollo.loading"
                        absolute
                        top
                        height="5"
                        background-color="blue"
                        color="red accent-4"
                ></v-progress-linear>

                <template v-slot:extension>

                    <v-tabs
                            v-model="tab"
                            align-with-title
                    >
                        <v-tabs-slider></v-tabs-slider>
                        <v-tab href="#overview">
                            Overview
                        </v-tab>

                        <v-tab href="#attendees">
                            <v-badge
                                    color="blue"
                                    :content="booking.attendees.length"
                            >
                                Attendees
                            </v-badge>
                        </v-tab>

                        <v-tab href="#billing">
                            Billing
                        </v-tab>
                        <v-tab href="#history">
                            History
                        </v-tab>
                    </v-tabs>

                    <v-btn
                            v-if="!readonly && !booking.completedAt"
                            :disabled="updating"
                            @click="markAsComplete"
                            color="purple"
                            dark
                            class="ma-2"
                            small
                    >
                        <v-icon dark left small>fas fa-thumbs-up</v-icon>Complete Booking</v-btn>
                    <v-btn
                            v-if="!readonly && booking.completedAt"
                            :disabled="updating"
                            @click="completeUpdateDialog = true"
                            color="purple"
                            dark
                            class="ma-2"
                            small
                    >
                        <v-icon dark left small>fas fa-thumbs-up</v-icon>Complete Update</v-btn>
                    <PythiaLink v-model="booking.id" />
                </template>
            </v-toolbar>
            <v-container class="grey lighten-3" fluid>
                <v-row>
                    <v-col cols="12">
                        <v-tabs-items :value="tab">
                            <v-tab-item value="overview">
                                <BookingOverview :model="booking" :readonly="readonly" @updating="updatingEvent"/>
                            </v-tab-item>
                            <v-tab-item value="attendees">
                                <BookingAttendees :booking="booking" :readonly="readonly" @refresh="refreshBooking"/>
                            </v-tab-item>
                            <v-tab-item value="billing">
                                <BookingBilling :model="booking"  :readonly="readonly" @updating="updatingEvent" />
                            </v-tab-item>
                            <v-tab-item value="history">
                                <BookingHistory :booking="booking" />
                            </v-tab-item>
                        </v-tabs-items>
                    </v-col>
                </v-row>
            </v-container>
        </div>

        <v-dialog
                v-model="completeUpdateDialog"
                max-width="550"
        >
            <v-card>
                <v-card-title class="headline">
                    Updating the client of a booking change.
                </v-card-title>

                <v-card-text>
                    Your about to update the client of a change in their booking, please select the reason:
                    <BookingUpdateNotes v-model="bookingUpdateNotes"/>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn
                            color="red darken-1"
                            :disabled="updating"
                            text
                            @click="completeUpdateDialog = false"
                    >
                        Close
                    </v-btn>

                    <v-btn
                            color="green darken-1"
                            :disabled="updating"
                            text
                            @click="completeUpdate"
                    >
                        Update Client
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import BOOKING_GET from "../graphql/BookingGet.gql"
    import BookingOverview from "../components/Booking/BookingOverview"
    import BookingBilling from "../components/Booking/BookingBilling"
    import BookingAttendees from "../components/Booking/BookingAttendees"
    import BookingUpdateNotes from "../components/Booking/Forms/BookingUpdateNotes";
    import PythiaLink from "../components/PythiaLink";
    import ApiMixin from "../mixins/ApiMixin";
    import {mapGetters} from "vuex";
    import UpdateStatusDialog from "../components/Booking/UpdateStatusDialog";
    import BookingHistory from "../components/Booking/BookingHistory";

    function initialBookingNotes(){
        return {
            courseChange: false,
            dateChange: false,
            delegateChange: false,
            levelChange: false,
            notes: null,
            poRefChange: false,
            priceChange: false,
            printedNotesChange: false,
        }
    }

    export default {
        name: "booking-view",
        components: {
            BookingHistory,
            UpdateStatusDialog, BookingOverview, BookingBilling, BookingAttendees, BookingUpdateNotes, PythiaLink},
        mixins: [ApiMixin],
        metaInfo() {
            return {
                title: this.title
            }
        },
        data() {
            return {
                title: `BK-` + this.$route.params.id,
                id: parseInt(this.$route.params.id),
                tab: this.$route.query.tab,
                updating: false,
                completeUpdateDialog: false,
                bookingUpdateNotes: initialBookingNotes()
            }
        },
        apollo: {
            booking: {
                query: BOOKING_GET,
                variables() {
                    return {id: parseInt(this.id)}
                },
                update: data => data.booking,
            },
        },
        watch: {
            tab(tab) {
                this.$router.replace({query: {...this.$route.query, tab}})
            },
        },
        computed: {
            ...mapGetters('auth', [
                'hasPermission',
            ]),
            readonly() {
                return !this.hasPermission('booking-update')
            },
        },
        methods: {
            updatingEvent(val) {
                this.updating = val
            },

            errorEvent(errors) {
                this.errored = true
                this.errors = errors
            },

            savedEvent() {
                this.saved = true
            },

            refreshBooking() {
                console.log('refreshing')
                this.$apollo.queries.booking.refetch()
            },

            markAsComplete() {
                this.updating = true
                this.submitEvent({booking: this.booking.id, name: 'booking-complete'}).then(() => {
                    this.updating = false
                    this.$store.dispatch('notification/addSuccess', 'Completed successfully')
                    this.refreshBooking()
                }).catch((error) => {
                    this.updating = false
                    this.$store.dispatch('notification/addError', error.message)
                    console.log(error)
                })
            },

            completeUpdate() {
                this.updating = true
                this.bookingUpdateNotes.bookingId = this.booking.id
                this.submitBookingNotes(this.bookingUpdateNotes).then((res) => {
                    console.log(res)
                    this.submitEvent({booking: this.booking.id, name: 'booking-update', bookingNote: res.data.bookingNote.id}).then(() => {
                        console.log(res)
                        this.updating = false
                        this.completeUpdateDialog = false
                        this.$store.dispatch('notification/addSuccess', 'Update completed successfully')
                        this.refreshBooking()
                        Object.assign(this.bookingUpdateNotes, initialBookingNotes());
                    }).catch((error) => {
                        this.updating = false
                        this.$store.dispatch('notification/addError', error.message)
                        console.log(error)
                    })
                }).catch((error) => {
                    this.updating = false
                    this.$store.dispatch('notification/addError', error.message)
                    console.log(error)
                })

            }
        }
    }
</script>